import { getDemandBreakdown, type DemandBreakdown, type DemandBreakdownChannel, type DemandBreakdownLine, type DemandSource } from '@api/replenishment-planning';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Alert, Badge, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useEffect, useMemo, useState, type FC, type ReactElement } from 'react';

import { dayLabel } from './dates';
import classes from './DemandWorkbook.module.css';

const SOURCE_LABEL: Record<DemandSource, string> = {
    DRR: 'Run rate',
    FORECAST: 'Forecast',
    MAX_DRR_FORECAST: 'Higher of run rate and forecast',
    OPEN_PO: 'Open POs',
    MAX_OPEN_PO_DRR: 'Open POs, else run rate',
    COMMITTED: 'Committed shipments',
    DRR_PLUS_COMMITTED: 'Run rate + committed',
    EXCLUDE: 'Excluded',
};

const n = (v: number, d = 0): string => formatCount(v, { maxDecimals: d });
const pct = (v: number): string => `${v > 0 ? '+' : ''}${formatCount(v, { maxDecimals: 1 })}%`;

type LineRow = DemandBreakdownLine & { channelKey: string; kind: 'Committed' | 'Open PO' };

/** Why a channel reads nothing in the window: the setting, or no sales and nothing dated. */
const zeroReason = (c: DemandBreakdownChannel): string => {
    if (c.source === 'EXCLUDE') return 'excluded in demand settings';
    if (c.source === 'COMMITTED' || c.source === 'OPEN_PO') return c.committed.length + c.openPo.length > 0 ? 'nothing due inside the window' : 'nothing committed or open';
    if (c.runRatePerDay === 0) return `no sales in the last ${Object.keys(c.drrByWindow).sort((a, b) => Number(b) - Number(a))[0] ?? ''} days`;
    return 'nothing in the window';
};

/**
 * The SKU's demand over the window the plan answers, one row per channel the brand sells on:
 * run rate × days, plus every committed shipment or open PO line due inside the window on its
 * own date, = the units the plan sizes against. This is the same day-by-day sum the engine
 * makes. Channels reading nothing are kept and flagged.
 */
const DemandBreakdownPanel: FC<{ productId: string; from?: string | null; to?: string | null; lead?: number; period?: { start: string | null; end: string | null } }> = ({ productId, from, to, lead = 0, period }) => {
    const [data, setData] = useState<DemandBreakdown | null>(null);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        let live = true;
        setData(null);
        setError(null);
        getDemandBreakdown(productId, from ?? undefined, to ?? undefined)
            .then((d) => live && setData(d))
            .catch((e: { error?: { message?: string }; message?: string }) => live && setError(e?.error?.message ?? e?.message ?? 'Could not read the demand'));
        return () => {
            live = false;
        };
    }, [productId, from, to]);

    const lines = useMemo<LineRow[]>(
        () =>
            (data?.channels ?? [])
                .flatMap((c) => [...c.committed.map((l) => ({ ...l, channelKey: c.channelKey, kind: 'Committed' as const })), ...c.openPo.map((l) => ({ ...l, channelKey: c.channelKey, kind: 'Open PO' as const }))])
                .sort((a, b) => Number(b.counted) - Number(a.counted) || a.date.localeCompare(b.date)),
        [data],
    );

    const days = data?.horizonDays ?? 0;
    const channelColumns = useMemo<DataSheetColumn<DemandBreakdownChannel>[]>(
        () => [
            {
                key: 'channel',
                header: 'Channel',
                width: 170,
                sticky: true,
                render: (_v, c): ReactElement => (
                    <Stack gap={0}>
                        <Text size="sm" fw={600}>
                            {c.channelKey}
                        </Text>
                        {c.total === 0 && (
                            <Badge size="xs" color="orange" variant="light" tt="none">
                                no demand · {zeroReason(c)}
                            </Badge>
                        )}
                    </Stack>
                ),
            },
            { key: 'source', header: 'Counts', width: 170, render: (_v, c): ReactElement => <Text size="sm" c="dimmed">{SOURCE_LABEL[c.source]}</Text> },
            {
                key: 'rate',
                header: 'Run rate / day',
                group: 'Run rate',
                width: 120,
                align: 'right',
                render: (_v, c): ReactElement => (
                    <Tooltip label={Object.entries(c.drrByWindow).map(([w, v]) => `${w}d: ${n(v, 1)}`).join(' · ') || 'no windows'} withArrow>
                        <Text size="sm" className={classes.num}>
                            {c.runRatePerDay ? n(c.runRatePerDay, 1) : '—'}
                        </Text>
                    </Tooltip>
                ),
            },
            {
                key: 'growth',
                header: 'Growth',
                group: 'Run rate',
                width: 100,
                align: 'right',
                render: (_v, c): ReactElement => <Text size="sm" c={c.channelGrowthPct || c.productGrowthPct ? undefined : 'dimmed'}>{c.channelGrowthPct || c.productGrowthPct ? `× ${n((1 + c.channelGrowthPct / 100) * (1 + c.productGrowthPct / 100), 3)}` : '—'}</Text>,
            },
            {
                key: 'grown',
                header: `× ${days} days`,
                group: 'Run rate',
                width: 110,
                align: 'right',
                render: (_v, c): ReactElement => <Text size="sm" className={classes.num}>{c.grownTotal ? n(c.grownTotal) : '—'}</Text>,
            },
            {
                key: 'firm',
                header: '+ Committed / open PO',
                width: 170,
                align: 'right',
                render: (_v, c): ReactElement => {
                    const counted = [...c.committed, ...c.openPo].filter((l) => l.counted).length;
                    return (
                        <Stack gap={0} align="flex-end">
                            <Text size="sm" className={classes.num}>
                                {c.firmTotal ? n(c.firmTotal) : '—'}
                            </Text>
                            {counted > 0 && (
                                <Text size="xs" c="dimmed">
                                    {counted} line{counted === 1 ? '' : 's'} due in the window
                                </Text>
                            )}
                        </Stack>
                    );
                },
            },
            { key: 'total', header: '= Units', width: 100, align: 'right', render: (_v, c): ReactElement => <Text size="sm" fw={700} className={classes.num}>{n(c.total)}</Text> },
            { key: 'avg', header: 'Avg / day', width: 100, align: 'right', render: (_v, c): ReactElement => <Text size="sm" c="dimmed" className={classes.num}>{days ? n(c.total / days, 1) : '—'}</Text> },
        ],
        [days],
    );
    const lineColumns = useMemo<DataSheetColumn<LineRow>[]>(
        () => [
            { key: 'date', header: 'Dispatch', width: 110, sortable: true, valueAccessor: (r) => r.date, render: (_v, r): ReactElement => <Text size="sm">{dayLabel(r.date)}</Text> },
            { key: 'channel', header: 'Channel', width: 130, render: (_v, r): ReactElement => <Text size="sm">{r.channelKey}</Text> },
            { key: 'kind', header: 'Kind', width: 110, render: (_v, r): ReactElement => <Badge size="xs" variant="light" color={r.kind === 'Committed' ? 'violet' : 'gray'} tt="none">{r.kind}</Badge> },
            { key: 'doc', header: 'Document', width: 170, render: (_v, r): ReactElement => <Text size="sm">{r.documentCode ?? '—'}</Text> },
            { key: 'dest', header: 'To', width: 180, render: (_v, r): ReactElement => <Text size="sm" c="dimmed">{r.destination ?? '—'}</Text> },
            { key: 'qty', header: 'Units', width: 100, align: 'right', sortable: true, valueAccessor: (r) => r.qty, render: (_v, r): ReactElement => <Text size="sm" fw={600} className={classes.num}>{n(r.qty)}</Text> },
            { key: 'counted', header: 'In the window', width: 130, render: (_v, r): ReactElement => <Text size="xs" c={r.counted ? undefined : 'dimmed'}>{r.counted ? 'counted' : 'outside, not counted'}</Text> },
        ],
        [],
    );

    if (error) return <Alert color="red">{error}</Alert>;
    if (!data) return <Loader size="sm" />;
    const grown = data.channels.reduce((s, c) => s + c.grownTotal, 0);
    const firm = data.channels.reduce((s, c) => s + c.firmTotal, 0);
    return (
        <Stack gap="md">
            <div className={classes.section}>
                <Text size="sm" className={classes.formula}>
                    <b>
                        {dayLabel(data.from)} → {dayLabel(data.to)}
                    </b>{' '}
                    ({days} days): run rate {n(grown)} + committed / open PO {n(firm)} = <b>{n(data.total)} units</b>
                </Text>
                <Text size="xs" c="dimmed">
                    {period?.start && period.end ? `The objective plans ${dayLabel(period.start)} → ${dayLabel(period.end)}. ` : ''}
                    {lead > 0 ? `A run lands ${lead} day${lead === 1 ? '' : 's'} after it starts, so this is the demand it answers. ` : ''}
                    The run rate counts on every day; a committed shipment counts once, on its dispatch date (from the in-transit-to-channel outbound), only when that date is inside the window.
                </Text>
            </div>
            <DataSheet<DemandBreakdownChannel>
                data={data.channels}
                columns={channelColumns}
                getRowKey={(c) => c.channelKey}
                rowHeight={48}
                hideHeaderBar
                maxHeight="auto"
                aggregateRow={{ channel: 'Total', grown: n(grown), firm: n(firm), total: n(data.total), avg: days ? n(data.total / days, 1) : '—' }}
            />
            {lines.length > 0 && (
                <div className={classes.section}>
                    <Text size="sm" fw={700}>
                        Committed shipments and open PO lines
                    </Text>
                    <DataSheet<LineRow> data={lines} columns={lineColumns} getRowKey={(r) => `${r.channelKey}|${r.kind}|${r.date}|${r.documentCode ?? ''}|${r.qty}`} rowHeight={36} hideHeaderBar maxHeight={280} />
                </div>
            )}
        </Stack>
    );
};

export default DemandBreakdownPanel;
